import { NumberRank } from "src/app/enums/number/number-rank.enum";
import { StyleNumber } from "src/app/enums/number/StyleNumber.enum";
import { Data } from "src/app/models/data/Data.interface";
import { DataOption } from "src/app/models/data/DataOption";
import { Unit } from "src/app/models/unit";
import { environment } from "src/environments/environment";
import { NumberUtil } from "./number.util";

/**
 * data = les valeurs reçues (par exemple d'api)
 * DateUtil aide à mapper les données et préparer leur affichage
 * L'interface Data représente un data : sous forme d'interface et pas de class
 * pour maximiser les perfs dans les pipes (cause pure: false)
 */

type numberOrNullOrUndefined = number | null | undefined;

export class DataUtil {

  // À utiliser lors du mapping d'un nombre. Crée un Data, utilisé ensuite par le pipe numberTranslate pour l'affichage
  static genDataFromApi(value: numberOrNullOrUndefined, unit?: Unit, rank?: NumberRank, currency?: string, fixed?: number): Data {
    return DataUtil.genData(value, NumberUtil.unitApiMapper(unit), rank, currency, fixed);
  }

  static genData(value: numberOrNullOrUndefined, style?: StyleNumber, rank?: NumberRank, currency?: string, fixed?: number): Data {
    const data: Data = {
      value: DataUtil.mapData(value),
      options: DataUtil.genDataOptions(style, fixed, currency, rank)
    }
    return data;
  }

  // map une valeur : dans un model, soit on récupère la valeur, soit on la passe à null
  static mapData(value: numberOrNullOrUndefined): number | null {
    return value !== null && value !== undefined ? value : null;
  }

  static genDataOptions(style?: StyleNumber, fixed?: number, currency?: string, rank?: NumberRank): DataOption {
    style = style || environment.core.data.defaultStyle;
    rank = rank || environment.core.data.defaultRank;
    return {
      style: style,
      fixed: fixed !== undefined ? fixed : NumberUtil.getFixedByStyle(style),
      currency: currency,
      rank: rank
    }
  }

  /**
   * retourne un nombre en string destiné à l'affichage
   * lang: BCP47 : format : xx-XX (pays-region) =>  https://fr.wiktionary.org/wiki/Wiktionnaire:BCP_47/language-2
   */
  static format(value: number, lang: string, options: DataOption): string {
    return NumberUtil.getNumber(value, lang, options.style, options.fixed, options.currency, options.rank);
  }

  // affichage d'un nombre à partir d'un Data
  static displayByData(data: Data | undefined, lang: string): string {
    if (data && data.value !== null) {
      return DataUtil.format(data.value, lang, data.options || {});
    } else {
      return this.displayNoData();
    }
  }

  // affichage d'un nombre sans passer par un Data
  static display(value: number | null, lang: string, options: DataOption = {}): string {
    if (value !== null) {
      return DataUtil.format(value, lang, options);
    } else {
      return this.displayNoData();
    }
  }

  // retourne la valeur à afficher si la valeur est inconnue
  static displayNoData(): string {
    return environment.core.noData;
  }
}
